import { APP_COMMIT_HASH } from '../config';
import { MINUTE, SECOND } from './dateFormat';

const CHECK_INTERVAL = MINUTE;
const IDLE_TIMEOUT = 30 * SECOND;
const ACTIVITY_THROTTLE = SECOND;
const USER_ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;
const LISTENER_OPTIONS: AddEventListenerOptions = { capture: true, passive: true };

interface AutoUpdateOptions {
  currentCommit?: string;
  checkInterval?: number;
  idleTimeout?: number;
  loadBuildInfo?: () => Promise<string | undefined>;
  reload?: NoneToVoidFunction;
  now?: () => number;
}

export function parseBuildCommit(buildInfo?: string): string | undefined {
  if (!buildInfo) return undefined;

  try {
    const parsed = JSON.parse(buildInfo) as { schemaVersion?: unknown; commit?: unknown };
    if (parsed.schemaVersion !== 1) return undefined;
    return normalizeCommit(parsed.commit);
  } catch {
    return undefined;
  }
}

export function initWebAppAutoUpdate({
  currentCommit = APP_COMMIT_HASH,
  checkInterval = CHECK_INTERVAL,
  idleTimeout = IDLE_TIMEOUT,
  loadBuildInfo = fetchBuildInfo,
  reload = () => window.location.reload(),
  now = Date.now,
}: AutoUpdateOptions = {}): NoneToVoidFunction {
  const normalizedCurrentCommit = normalizeCommit(currentCommit);
  if (!normalizedCurrentCommit) return () => {};

  let lastActivityAt = now();
  let isUpdatePending = false;
  let isCheckInProgress = false;
  let isDisposed = false;
  let isReloading = false;
  let reloadTimer: number | undefined;

  const clearReloadTimer = () => {
    if (reloadTimer !== undefined) {
      window.clearTimeout(reloadTimer);
      reloadTimer = undefined;
    }
  };

  const reloadOnce = () => {
    if (isDisposed || isReloading) return;
    isReloading = true;
    clearReloadTimer();
    reload();
  };

  const scheduleReload = () => {
    clearReloadTimer();
    if (!isUpdatePending || isDisposed || isReloading) return;

    if (document.hidden) {
      reloadOnce();
      return;
    }

    const remainingIdleTime = idleTimeout - (now() - lastActivityAt);
    if (remainingIdleTime <= 0) {
      reloadOnce();
      return;
    }

    reloadTimer = window.setTimeout(scheduleReload, remainingIdleTime);
  };

  const checkForUpdate = async () => {
    if (isDisposed || isCheckInProgress || isReloading) return;
    isCheckInProgress = true;

    try {
      const latestCommit = parseBuildCommit(await loadBuildInfo());
      if (!latestCommit) return;

      isUpdatePending = latestCommit !== normalizedCurrentCommit;
      scheduleReload();
    } catch {
      // Temporary network errors are expected; the next interval or `online` event retries the check.
    } finally {
      isCheckInProgress = false;
    }
  };

  const handleUserActivity = () => {
    const timestamp = now();
    if (timestamp - lastActivityAt < ACTIVITY_THROTTLE) return;

    lastActivityAt = timestamp;
    if (isUpdatePending) scheduleReload();
  };

  const handleVisibilityChange = () => {
    if (isUpdatePending) scheduleReload();
    void checkForUpdate();
  };

  USER_ACTIVITY_EVENTS.forEach((eventName) => {
    document.addEventListener(eventName, handleUserActivity, LISTENER_OPTIONS);
  });
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('online', checkForUpdate);

  const checkTimer = window.setInterval(checkForUpdate, checkInterval);
  void checkForUpdate();

  return () => {
    isDisposed = true;
    clearReloadTimer();
    window.clearInterval(checkTimer);
    USER_ACTIVITY_EVENTS.forEach((eventName) => {
      document.removeEventListener(eventName, handleUserActivity, LISTENER_OPTIONS);
    });
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('online', checkForUpdate);
  };
}

async function fetchBuildInfo(): Promise<string | undefined> {
  const url = resolveBuildInfoUrl();
  url.searchParams.set('_', String(Date.now()));

  const response = await fetch(url, { cache: 'no-store' });
  return response.ok ? response.text() : undefined;
}

function resolveBuildInfoUrl() {
  const appScript = Array.from(document.scripts).reverse().find(({ src }) => {
    if (!src) return false;
    const url = new URL(src, document.baseURI);
    return url.origin === window.location.origin && /\/main(?:\.[^/]+)?\.js$/.test(url.pathname);
  });

  return new URL('build.json', appScript?.src || document.baseURI);
}

function normalizeCommit(commit: unknown) {
  if (typeof commit !== 'string') return undefined;
  const normalized = commit.trim().toLowerCase();
  return /^[a-f\d]{7,64}$/.test(normalized) ? normalized : undefined;
}
